import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@/providers/AuthProvider';
import { USER_ROLES, type UserRole } from '@/lib/auth';
import logo from '@/assets/logo.jpg';
import { PillButton } from '@/components/entity';
import {
  CalendarIcon,
  Cross2Icon,
  DashboardIcon,
  EnvelopeClosedIcon,
  GearIcon,
  HamburgerMenuIcon,
  HomeIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import { Popover, PopoverAnchor, PopoverContent, PopoverPortal } from '@radix-ui/react-popover';
import { useLayoutMetrics } from '@/components/layout';
import { CommandPalette, type CommandPaletteItem } from '@/components/dashboard/CommandPalette';

const NAV_ITEMS: Array<{
  id: string;
  label: string;
  path: string;
  roles: readonly UserRole[];
  icon: (props: { className?: string }) => JSX.Element;
}> = [
  {
    id: 'overview',
    label: 'Dashboard',
    path: '/dashboard',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    icon: (props) => <DashboardIcon {...props} />,
  },
  {
    id: 'leads',
    label: 'Leads',
    path: '/dashboard/leads',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    icon: (props) => <EnvelopeClosedIcon {...props} />,
  },
  {
    id: 'availability-alerts',
    label: 'Availability Alerts',
    path: '/dashboard/availability-alerts',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    icon: (props) => <EnvelopeClosedIcon {...props} />,
  },
  {
    id: 'showing-availability',
    label: 'Showing Availability',
    path: '/dashboard/showings/availability',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    icon: (props) => <CalendarIcon {...props} />,
  },
  {
    id: 'showing-requests',
    label: 'Showing Requests',
    path: '/dashboard/showings/requests',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    icon: (props) => <CalendarIcon {...props} />,
  },
  {
    id: 'properties',
    label: 'Properties',
    path: '/dashboard/properties',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    icon: (props) => <HomeIcon {...props} />,
  },
  {
    id: 'tenant-profiles',
    label: 'Tenant Profiles',
    path: '/dashboard/tenant-profiles',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    icon: (props) => <PersonIcon {...props} />,
  },
  {
    id: 'site-settings',
    label: 'Site Settings',
    path: '/dashboard/site-settings',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    icon: (props) => <GearIcon {...props} />,
  },
  {
    id: 'page-settings',
    label: 'Page Settings',
    path: '/dashboard/page-settings',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    icon: (props) => <GearIcon {...props} />,
  },
];

const COMMAND_ITEMS: Array<CommandPaletteItem & { roles: readonly UserRole[] }> = [
  {
    id: 'command-dashboard',
    label: 'Dashboard overview',
    description: 'Return to the main dashboard summary.',
    href: '/dashboard',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['home', 'overview', 'summary'],
  },
  {
    id: 'command-leads',
    label: 'Leads',
    description: 'Review leads with scheduled tours.',
    href: '/dashboard/leads',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['pipeline', 'contacts'],
  },
  {
    id: 'command-showing-requests',
    label: 'Showing requests',
    description: 'Approve and reschedule tours.',
    href: '/dashboard/showings/requests',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['tours', 'requests', 'approvals'],
  },
  {
    id: 'command-showing-availability',
    label: 'Showing availability',
    description: 'Manage weekly availability slots.',
    href: '/dashboard/showings/availability',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['slots', 'schedule'],
  },
  {
    id: 'command-alerts',
    label: 'Availability alerts',
    description: 'Review alert subscribers and activity.',
    href: '/dashboard/availability-alerts',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['alerts', 'subscribers'],
  },
  {
    id: 'command-properties',
    label: 'Properties',
    description: 'Audit inventory across the portfolio.',
    href: '/dashboard/properties',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['inventory', 'portfolio'],
  },
  {
    id: 'command-new-property',
    label: 'Create property',
    description: 'Add a new property to the portfolio.',
    href: '/dashboard/properties/new',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['new', 'create', 'property'],
  },
  {
    id: 'command-tenant-profiles',
    label: 'Tenant profiles',
    description: 'Preview the 501 Alliance tenant roster.',
    href: '/dashboard/tenant-profiles',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['tenants', 'collective', 'roster', 'profiles'],
  },
  {
    id: 'command-site-settings',
    label: 'Site settings',
    description: 'Update global brand and contact details.',
    href: '/dashboard/site-settings',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['cms', 'brand'],
  },
  {
    id: 'command-page-settings',
    label: 'Page settings',
    description: 'Edit homepage and public copy.',
    href: '/dashboard/page-settings',
    roles: [USER_ROLES.administrator, USER_ROLES.manager],
    keywords: ['cms', 'copy'],
  },
];

type NavItem = (typeof NAV_ITEMS)[number];

interface DashboardShellProps {
  children: ReactNode;
}

export function isNavItemActive(itemPath: string, currentPath: string): boolean {
  const normalizedItem =
    itemPath.endsWith('/') && itemPath !== '/' ? itemPath.slice(0, -1) : itemPath;
  const normalizedCurrent =
    currentPath.endsWith('/') && currentPath !== '/' ? currentPath.slice(0, -1) : currentPath;

  if (normalizedItem === '/dashboard') {
    return normalizedCurrent === normalizedItem;
  }

  return normalizedCurrent === normalizedItem || normalizedCurrent.startsWith(`${normalizedItem}/`);
}

interface DashboardNavItemProps {
  item: NavItem;
  isActive: boolean;
  isDesktopCollapsed: boolean;
  onSelect: () => void;
}

function DashboardNavItem({
  item,
  isActive,
  isDesktopCollapsed,
  onSelect,
}: DashboardNavItemProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const popoverId = `dashboard-nav-${useId()}`;
  const Icon = item.icon;
  const linkClassName = clsx(
    'flex w-full flex-row items-center gap-3 border-x-0 border-y border-transparent px-3 py-3 text-sm font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary md:items-center md:justify-start md:px-4 md:py-3',
    isActive
      ? 'border-y-brand-primary/40 bg-surface-cream text-brand-primary'
      : 'bg-white text-brand-primary/80 hover:border-y-brand-primary/40 hover:bg-brand-primary/10 hover:text-brand-primary',
    isDesktopCollapsed && 'md:justify-center md:px-2',
  );
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const navLink = (
    <NavLink
      to={item.path}
      className={linkClassName}
      onClick={onSelect}
      aria-haspopup={isDesktopCollapsed ? 'dialog' : undefined}
      aria-controls={isDesktopCollapsed ? popoverId : undefined}
      aria-expanded={isDesktopCollapsed ? open : undefined}
      onMouseEnter={isDesktopCollapsed ? handleOpen : undefined}
      onMouseLeave={isDesktopCollapsed ? handleClose : undefined}
      onFocus={isDesktopCollapsed ? handleOpen : undefined}
      onBlur={isDesktopCollapsed ? handleClose : undefined}
    >
      <Icon
        aria-hidden="true"
        className={clsx(
          'h-5 w-5 flex-shrink-0 text-brand-primary',
          !isDesktopCollapsed && 'md:text-brand-primary/80',
        )}
      />
      <span
        className={clsx(
          'inline-flex items-center whitespace-nowrap text-sm font-semibold tracking-wide',
          'md:text-xs md:uppercase',
          isDesktopCollapsed && 'md:hidden',
        )}
      >
        {item.label}
      </span>
    </NavLink>
  );

  if (!isDesktopCollapsed) {
    return navLink;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>{navLink}</PopoverAnchor>
      <PopoverPortal>
        <PopoverContent
          id={popoverId}
          side="right"
          align="center"
          sideOffset={8}
          collisionPadding={12}
          aria-label={item.label}
          className="rounded-md border border-brand-primary/30 bg-brand-primary px-2 py-1 text-xs font-medium text-white shadow-card"
        >
          {item.label}
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}

export function DashboardShell({ children }: DashboardShellProps): JSX.Element {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isPinned, setIsPinned] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const sidebarId = 'dashboard-shell-sidebar';
  const topbarRef = useRef<HTMLElement | null>(null);
  const sidebarStyle = useMemo(
    () => ({
      minHeight:
        'calc(var(--layout-safe-viewport, 100vh) - var(--layout-header-height, 0px) - var(--layout-footer-height, 0px))',
      maxHeight:
        'calc(var(--layout-safe-viewport, 100vh) - var(--layout-header-height, 0px) - var(--layout-footer-height, 0px))',
    }),
    [],
  );

  const isDesktopCollapsed = !isPinned;
  const toggleLabel = isPinned ? 'Collapse menu' : 'Show menu';
  const toggleShortLabel = isPinned ? 'Collapse' : 'Expand';
  const { reportTopbarHeight } = useLayoutMetrics();

  const navigation = useMemo(() => {
    if (!user) {
      return [] as typeof NAV_ITEMS;
    }
    return NAV_ITEMS.filter((item) => item.roles.includes(user.role));
  }, [user]);

  const commandItems = useMemo(() => {
    if (!user) {
      return [];
    }
    return COMMAND_ITEMS.filter((item) => item.roles.includes(user.role)).map(
      ({ roles: _roles, ...item }) => item,
    );
  }, [user]);

  const activeLabel = useMemo(() => {
    const current = navigation.find((item) => isNavItemActive(item.path, location.pathname));
    return current?.label ?? 'Dashboard';
  }, [location.pathname, navigation]);

  const userLabel = useMemo(() => user?.displayName ?? user?.email ?? 'Account', [user]);
  const sidebarWidthVar = isDesktopCollapsed
    ? 'var(--layout-shell-sidebar-collapsed, 4.5rem)'
    : 'var(--layout-shell-sidebar, 18rem)';
  const layoutStyle = useMemo<CSSProperties>(
    () =>
      ({
        '--dashboard-shell-sidebar-width': sidebarWidthVar,
      }) as CSSProperties,
    [sidebarWidthVar],
  );
  useEffect(() => {
    if (!mobileOpen || typeof document === 'undefined') {
      return;
    }

    const { style } = document.body;
    const previousOverflow = style.overflow;
    style.overflow = 'hidden';

    return () => {
      style.overflow = previousOverflow;
    };
  }, [mobileOpen]);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.key || event.key.toLowerCase() !== 'k') {
        return;
      }
      if (!(event.metaKey || event.ctrlKey)) {
        return;
      }
      const target = event.target as HTMLElement | null;
      const isEditable =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        target?.isContentEditable;
      if (isEditable) {
        return;
      }
      event.preventDefault();
      setCommandOpen(true);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);
  const toggleMobileMenu = useCallback(() => {
    setMobileOpen((previous) => !previous);
  }, []);
  const togglePinned = useCallback(() => {
    setIsPinned((prev) => !prev);
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      closeMobile();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Sign out failed', error);
      }
    }
  }, [closeMobile, signOut]);

  useLayoutEffect(() => {
    const topbarElement = topbarRef.current;

    if (!topbarElement) {
      reportTopbarHeight(0);
      return;
    }

    const updateHeight = () => {
      reportTopbarHeight(Math.round(topbarElement.getBoundingClientRect().height));
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        reportTopbarHeight(0);
      };
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(topbarElement);

    return () => {
      observer.disconnect();
      reportTopbarHeight(0);
    };
  }, [reportTopbarHeight]);

  return (
    <div
      className="relative flex h-full min-h-0 w-full flex-1 flex-col bg-surface-light md:grid md:grid-cols-[var(--dashboard-shell-sidebar-width,18rem)_1fr]"
      style={layoutStyle}
      data-dashboard-shell="true"
    >
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={closeMobile}
        />
      ) : null}

      <aside
        aria-label="Dashboard navigation"
        id={sidebarId}
        className={clsx(
          'z-40 flex max-w-full flex-col border-r border-surface-subtle bg-white/95 shadow-xl backdrop-blur transition-[transform,width] duration-200 md:z-auto md:shadow-none md:backdrop-blur-none',
          'md:sticky md:top-0 md:h-full md:max-h-full md:overflow-y-auto md:transition-[width] md:duration-150',
          mobileOpen
            ? 'fixed inset-y-0 left-0 w-[min(20rem,90vw)] translate-x-0 md:relative'
            : 'fixed inset-y-0 left-0 w-[min(20rem,90vw)] -translate-x-full md:relative md:translate-x-0',
          'md:w-[var(--dashboard-shell-sidebar-width,18rem)]',
        )}
        style={sidebarStyle}
      >
        <div
          className="relative flex items-center justify-center border-b border-surface-subtle px-4 py-0"
          style={{
            // Mirror the measured dashboard header height so these surfaces stay visually aligned.
            minHeight: 'max(var(--layout-dashboard-topbar, 0px), 4rem)',
            paddingBlock: 'calc((max(var(--layout-dashboard-topbar, 0px), 4rem) - 3rem - 1px) / 2)',
          }}
        >
          <img
            src={logo}
            alt="Strashin Developments"
            className="h-12 w-auto rounded-lg border border-brand-primary/10 shadow-sm"
          />
          <button
            type="button"
            className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-brand-primary/40 text-xl text-text-primary/70 transition hover:bg-brand-primary/10 md:hidden"
            onClick={closeMobile}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-xs overflow-y-auto py-4">
          {navigation.map((item) => (
            <DashboardNavItem
              key={item.id}
              item={item}
              isActive={isNavItemActive(item.path, location.pathname)}
              isDesktopCollapsed={isDesktopCollapsed}
              onSelect={() => {
                if (!isPinned) {
                  setIsPinned(true);
                }
                closeMobile();
              }}
            />
          ))}
        </nav>

        <div className="border-t border-surface-subtle px-4 py-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col">
              <span className="text-xs font-medium uppercase tracking-wide text-text-primary/50">
                Signed in as
              </span>
              <span className="text-sm font-semibold text-text-primary">{userLabel}</span>
            </div>
            <PillButton className="w-full" onClick={handleSignOut}>
              Log out
            </PillButton>
            <PillButton
              variant="secondary"
              size="md"
              className={clsx(
                'hidden w-full font-medium md:flex md:items-center md:justify-center',
                isDesktopCollapsed && 'md:px-0',
              )}
              onClick={togglePinned}
              aria-expanded={isPinned}
            >
              <span className="sr-only">{toggleLabel}</span>
              {isDesktopCollapsed ? (
                <span aria-hidden="true" className="text-xs uppercase tracking-wide">
                  {toggleShortLabel}
                </span>
              ) : (
                <span aria-hidden="true">{toggleLabel}</span>
              )}
            </PillButton>
          </div>
        </div>
      </aside>

      <div className="flex h-full min-h-0 flex-1 flex-col md:col-start-2 md:overflow-hidden">
        <header
          ref={topbarRef}
          className="sticky top-0 z-20 flex items-center gap-3 border-b border-surface-subtle bg-surface-light/95 px-4 py-4 shadow-sm backdrop-blur md:px-8"
        >
          <button
            type="button"
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={mobileOpen}
            aria-controls={sidebarId}
            onClick={toggleMobileMenu}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-primary/40 bg-white/70 text-brand-primary shadow-sm transition hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary md:hidden"
          >
            {mobileOpen ? (
              <Cross2Icon className="h-5 w-5" />
            ) : (
              <HamburgerMenuIcon className="h-5 w-5" />
            )}
          </button>
          <span className="text-sm font-semibold text-text-primary md:text-base">
            {activeLabel}
          </span>
          <div className="ml-auto flex items-center gap-3">
            <PillButton
              variant="secondary"
              size="sm"
              className="hidden md:inline-flex"
              onClick={() => {
                setCommandOpen(true);
              }}
            >
              Command ⌘K
            </PillButton>
            <span className="hidden text-sm font-semibold text-brand-primary md:inline">
              {userLabel}
            </span>
            <PillButton variant="secondary" size="sm" onClick={handleSignOut}>
              Log out
            </PillButton>
          </div>
        </header>
        {!isPinned ? (
          <div className="hidden justify-end px-4 py-4 md:flex">
            <PillButton
              variant="secondary"
              size="sm"
              className="font-medium"
              onClick={() => {
                setIsPinned(true);
              }}
            >
              Show menu
            </PillButton>
          </div>
        ) : null}
        <main className="flex min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-6">
          <div className="dashboard-shell__content">{children}</div>
        </main>
      </div>

      <CommandPalette
        open={commandOpen}
        onOpenChange={setCommandOpen}
        items={commandItems}
        onSelect={(item) => {
          setCommandOpen(false);
          navigate(item.href);
        }}
      />
    </div>
  );
}
